import { AlertDialog } from '@base-ui/react/alert-dialog';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import buttonStyles from '@/admin/styles/button.module.css';

type Tone = 'warning' | 'danger' | 'primary';

const TONE_COLORS: Record<Tone, string> = {
  warning: 'var(--orange)',
  danger: 'var(--error)',
  primary: 'var(--blue)',
};

// Asked before a change that can't be taken back.
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel,
  tone = 'warning',
  pending = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: Tone;
  // The confirmed action is running: one click, one request.
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <AlertDialog.Portal>
        <Backdrop />
        <Popup $color={TONE_COLORS[tone]}>
          <Title>{title}</Title>
          <Description render={<div />}>{children}</Description>
          <DialogActions>
            <AlertDialog.Close className={buttonStyles.Button}>
              {cancelLabel ?? t('admin.common.goBack')}
            </AlertDialog.Close>
            <ConfirmButton
              type="button"
              $color={TONE_COLORS[tone]}
              className={buttonStyles.Button}
              disabled={pending}
              onClick={onConfirm}
            >
              {confirmLabel}
            </ConfirmButton>
          </DialogActions>
        </Popup>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

const Backdrop = styled(AlertDialog.Backdrop)`
  background-color: rgba(0, 0, 0, 0.5);
  inset: 0;
  position: fixed;
  z-index: 100;
`;

const Popup = styled(AlertDialog.Popup)<{ $color: string }>`
  background-color: var(--component-color);
  border-radius: 0.5rem;
  border-top: 4px solid ${(p) => p.$color};
  box-shadow: var(--shadow-popover);
  display: flex;
  flex-direction: column;
  left: 50%;
  max-width: 90vw;
  padding: 1.5rem;
  position: fixed;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 460px;
  z-index: 101;
`;

const Title = styled(AlertDialog.Title)`
  color: var(--grey-900);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
`;

const Description = styled(AlertDialog.Description)`
  color: var(--grey-800);
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 1.25rem;

  p {
    margin: 0 0 0.5rem;
  }

  ul {
    margin: 0 0 0.5rem;
    padding-left: 1.25rem;
  }
`;

const DialogActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

// Doubled class to win over the shared button's own hover background.
const ConfirmButton = styled.button<{ $color: string }>`
  && {
    background-color: ${(p) => p.$color};
    border-color: ${(p) => p.$color};
    color: var(--white);
    cursor: pointer;
    font-weight: 600;
  }

  &&:hover:not(:disabled) {
    background-color: color-mix(in srgb, ${(p) => p.$color} 88%, black);
  }

  &&:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;
